import React, { useState, useEffect, useCallback } from 'react';
import {
  Star,
  ThumbsUp,
  Eye,
  TrendingUp,
  Share2
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { useNavigate } from 'react-router-dom';
import './TalentDashboard.css';
import { API_BASE_URL } from '../../utils/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Define the interface for dashboard stats
interface DashboardStats {
  profileViews: number;
  contactClickthroughs: number;
  averageRating: number;
  totalReviews: number;
  shareClicks?: number;
  totalInquiries?: number;
  respondedInquiries?: number;
  responseRate?: number;
  totalClients?: number;
  activeClients?: number;
  totalServices?: number;
  activeServices?: number;
  monthlyEarnings?: number;
  totalEarnings?: number;
}

interface ChartData {
  inquiries: Array<{ date: string; received: number; responded: number }>;
  bookings: Array<{ date: string; count: number; revenue: number }>;
  reviews: Array<{ date: string; count: number; average_rating: number }>;
  earnings: Array<{ date: string; amount: number }>;
}

interface ActivityItem {
  id: number;
  type: 'booking' | 'review' | 'payment' | 'inquiry';
  message: string;
  timestamp: string;
  client_name?: string;
  client_image?: string;
}

interface ServiceStats {
  totalServices: number;
  activeServices: number;
  servicesByCategory: Array<{ category: string; service_count: number }>;
  topServices: Array<{ title: string; price: number; booking_count: number; avg_rating: number }>;
}

const TalentDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [engagementData, setEngagementData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [serviceStats, setServiceStats] = useState<ServiceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const activeTab = 'analytics'; // Fixed to analytics
  const [membershipStatus, setMembershipStatus] = useState<{
    hasPaidMembership: boolean;
    transactionActive: boolean;
  }>({
    hasPaidMembership: false,
    transactionActive: false
  });

  const { talent, isLoading: authLoading } = useTalentAuth();
  const navigate = useNavigate();

  // Fetch membership status
  const fetchMembershipStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/membership/transaction-check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMembershipStatus({
            hasPaidMembership: data.hasPaidMembership || false,
            transactionActive: data.hasPaidMembership && data.transaction && data.transaction.isActive
          });
        }
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
      setMembershipStatus({
        hasPaidMembership: false,
        transactionActive: false
      });
    }
  }, []);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/talent-dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch stats: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
        setEngagementData(data.engagementComparison || []);
      } else {
        throw new Error(data.message || 'Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch stats');
    }
  }, []);

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/talent-dashboard/chart-data?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChartData(data.chartData);
        }
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  }, [selectedPeriod]);

  // Fetch recent activities
  const fetchActivities = useCallback(async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/talent-dashboard/activity-feed?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setActivities(data.activities);
        }
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, []);

  // Fetch service statistics
  const fetchServiceStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/talent-dashboard/services-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setServiceStats(data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching service stats:', error);
    }
  }, []);

  // Load all dashboard data
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchStats(),
        fetchChartData(),
        fetchActivities(),
        fetchServiceStats(),
        fetchMembershipStatus()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchChartData, fetchActivities, fetchServiceStats, fetchMembershipStatus]);

  // Initial load
  useEffect(() => {
    if (!authLoading) {
      if (talent) {
        loadDashboardData();
      } else {
        // Auth has finished loading but no talent found - redirect to login
        navigate('/talent/login');
      }
    }
  }, [talent, authLoading, loadDashboardData, navigate]);

  // Reload chart data when period changes
  useEffect(() => {
    if (talent && !loading) {
      fetchChartData();
    }
  }, [selectedPeriod, talent, loading, fetchChartData]);

  const handleRetry = () => {
    loadDashboardData();
  };

  // Utility functions
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'booking': return '📅';
      case 'review': return '⭐';
      case 'payment': return '💰';
      case 'inquiry': return '💬';
      default: return '📋';
    }
  };

  // Show loading while auth is still loading
  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Authenticating...</p>
      </div>
    );
  }

  // Redirect if not authenticated (this should not be reached due to useEffect redirect)
  if (!talent && !authLoading) {
    console.log('No talent found, redirecting to login');
    navigate('/talent/login');
    return null;
  }

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-content">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
  <div className="talent-dashboard-inner">
      {/* Membership Blur Overlay */}
      {/* {!membershipStatus.hasPaidMembership && (
        <div className="membership-blur-overlay">
          <div className="blur-backdrop"></div>
          <div className="upgrade-prompt">
            <div className="upgrade-content">
              <div className="upgrade-icon">👑</div>
              <h2>Premium Membership Required</h2>
              <p>Unlock your full dashboard with detailed analytics, performance metrics, and business insights.</p>
              <div className="upgrade-features">
                <div className="feature-item">✅ Detailed Performance Analytics</div>
                <div className="feature-item">✅ Client & Revenue Tracking</div>
                <div className="feature-item">✅ Service Performance Metrics</div>
                <div className="feature-item">✅ Real-time Activity Feed</div>
              </div>
              <button 
                className="upgrade-btn-large"
                onClick={() => navigate('/talent/membership')}
              >
                Upgrade to Premium - $10/month
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* <div className={`container-fluid ${!membershipStatus.hasPaidMembership ? 'blurred-content' : ''}`}> */}
      <div className={`container-fluid`}>
        {/* Dashboard Header */}
        {/* <div className="dashboard-header">
          <div className="header-content">
            <div className="header-info">
              <h1>Welcome back, {talent?.firstName}! 👋</h1>
              <p>Here's what's happening with your business today</p>
            </div>
            <div className="header-controls">
              <select 
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="period-selector"
              >
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="90days">Last 90 days</option>
              </select>
              <button onClick={handleRetry} className="refresh-btn">
                🔄 Refresh
              </button>
            </div>
          </div>
        </div> */}

        <div className="row">

          {/* Main Content */}
          <div className="col-md-12">
            {activeTab === 'analytics' && (
              <div className="dashboard-body-content">
                {/* Enhanced Stats Grid */}
                <div className="stats-grid enhanced">
                  <div className="stat-card primary">
                    <div className="stat-icon">
                      <Eye size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Profile Views</h3>
                      <p className="stat-value">{!membershipStatus.hasPaidMembership ? 0 : stats?.profileViews || 0}</p>
                      <p className="stat-label">Last 30 days</p>
                    </div>
                  </div>

                  <div className="stat-card success">
                    <div className="stat-icon">
                      <TrendingUp size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Contact Clicks</h3>
                      <p className="stat-value">{!membershipStatus.hasPaidMembership ? 0 : stats?.contactClickthroughs || 0}</p>
                      <p className="stat-label">
                        {stats?.profileViews && stats?.profileViews > 0
                          ? Math.round((stats.contactClickthroughs / stats.profileViews) * 100)
                          : 0}% conversion rate
                      </p>
                    </div>
                  </div>

                  <div className="stat-card share-clicks">
                    <div className="stat-icon">
                      <Share2 size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Share Button Clicks</h3>
                      <p className="stat-value">{!membershipStatus.hasPaidMembership ? 0 : stats?.shareClicks || 0}</p>
                      <p className="stat-label">Total share clicks</p>
                    </div>
                  </div>

                  <div className="stat-card warning">
                    <div className="stat-icon">
                      <Star size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Average Rating</h3>
                      <p className="stat-value">{stats?.averageRating || 0}/5.0</p>
                      <p className="stat-label">From {stats?.totalReviews || 0} reviews</p>
                    </div>
                  </div>

                  <div className="stat-card info">
                    <div className="stat-icon">
                      <ThumbsUp size={24} />
                    </div>
                    <div className="stat-content">
                      <h3>Total Services</h3>
                      <p className="stat-value">{serviceStats?.totalServices || 0}</p>
                      <p className="stat-label">{serviceStats?.activeServices || 0} active</p>
                    </div>
                  </div>

                  {stats?.totalInquiries !== undefined && (
                    <div className="stat-card secondary">
                      <div className="stat-icon">
                        💬
                      </div>
                      <div className="stat-content">
                        <h3>Response Rate</h3>
                        <p className="stat-value">{stats?.responseRate || 0}%</p>
                        <p className="stat-label">{stats?.respondedInquiries || 0} of {stats?.totalInquiries || 0} inquiries</p>
                      </div>
                    </div>
                  )}

                  {stats?.monthlyEarnings !== undefined && (
                    <div className="stat-card earnings">
                      <div className="stat-icon">
                        💰
                      </div>
                      <div className="stat-content">
                        <h3>Monthly Earnings</h3>
                        <p className="stat-value">{formatCurrency(stats?.monthlyEarnings || 0)}</p>
                        <p className="stat-label">Total: {formatCurrency(stats?.totalEarnings || 0)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enhanced Charts Section */}
                <div className="analytics-charts enhanced" style={{ marginTop: '30px' }}>
                  <div className="row">
                    {/* Performance Overview */}
                    <div className="col-md-8">
                      <div className="chart-card large">
                        <div className="chart-header">
                          <h4>📊 Performance Overview</h4>
                          <span className="chart-subtitle">Track your business metrics over time</span>
                        </div>
                        <div className="chart-container">
                          {chartData?.inquiries ? (
                            <div className="performance-chart">
                              <div className="chart-metrics">
                                <div className="metric-item">
                                  <div className="metric-label">Total Inquiries</div>
                                  <div className="metric-value">
                                    {chartData.inquiries.reduce((sum, item) => sum + item.received, 0)}
                                  </div>
                                </div>
                                <div className="metric-item">
                                  <div className="metric-label">Response Rate</div>
                                  <div className="metric-value">
                                    {chartData.inquiries.length > 0
                                      ? Math.round((chartData.inquiries.reduce((sum, item) => sum + item.responded, 0) /
                                        chartData.inquiries.reduce((sum, item) => sum + item.received, 0)) * 100)
                                      : 0}%
                                  </div>
                                </div>
                                <div className="metric-item">
                                  <div className="metric-label">Total Bookings</div>
                                  <div className="metric-value">
                                    {chartData.bookings?.reduce((sum, item) => sum + item.count, 0) || 0}
                                  </div>
                                </div>
                              </div>
                              <div className="simple-line-chart">
                                {chartData.inquiries.slice(-7).map((item, index) => (
                                  <div key={index} className="chart-bar">
                                    <div className="bar-container">
                                      <div
                                        className="bar received"
                                        style={{ height: `${(item.received / 10) * 100}%` }}
                                        title={`${item.received} inquiries received`}
                                      ></div>
                                      <div
                                        className="bar responded"
                                        style={{ height: `${(item.responded / 10) * 100}%` }}
                                        title={`${item.responded} inquiries responded`}
                                      ></div>
                                    </div>
                                    <div className="bar-label">{new Date(item.date).getDate()}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="chart-placeholder">
                              <p>📈 Performance data will appear here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="col-md-4">
                      <div className="chart-card">
                        <div className="chart-header">
                          <h4>🎯 Quick Stats</h4>
                          <span className="chart-subtitle">Key metrics at a glance</span>
                        </div>
                        <div className="quick-stats">
                          <div className="quick-stat-item">
                            <div className="stat-circle blue">
                              <span className="stat-number">{stats?.totalClients || 0}</span>
                            </div>
                            <div className="stat-info">
                              <div className="stat-title">Total Clients</div>
                              <div className="stat-subtitle">{stats?.activeClients || 0} active</div>
                            </div>
                          </div>

                          <div className="quick-stat-item">
                            <div className="stat-circle green">
                              <span className="stat-number">{Math.round(stats?.averageRating || 0)}</span>
                            </div>
                            <div className="stat-info">
                              <div className="stat-title">Rating</div>
                              <div className="stat-subtitle">out of 5 stars</div>
                            </div>
                          </div>

                          <div className="quick-stat-item">
                            <div className="stat-circle orange">
                              <span className="stat-number">
                                {stats?.profileViews && stats?.profileViews > 0
                                  ? Math.round((stats.contactClickthroughs / stats.profileViews) * 100)
                                  : 0}%
                              </span>
                            </div>
                            <div className="stat-info">
                              <div className="stat-title">Conversion</div>
                              <div className="stat-subtitle">view to contact</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Engagement Comparison */}
                  <div className="row" style={{ marginTop: '20px' }}>
                    {/* Last Month Engagement */}
                    <div className="col-md-12">
                      <div className="chart-card large" style={{ minHeight: '400px' }}>
                        <div className="chart-header">
                          <h4>📈 Last Month Engagement</h4>
                          <span className="chart-subtitle">Profile views and share clicks trend for last month</span>
                        </div>
                        <div className="chart-container" style={{ height: '300px', width: '100%', marginTop: '20px' }}>
                          {engagementData && engagementData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={engagementData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="day" tick={{ fill: '#718096', fontSize: 12 }} />
                                <YAxis tick={{ fill: '#718096', fontSize: 12 }} />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                  }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                <Line 
                                  type="monotone" 
                                  dataKey="lastMonthViews" 
                                  stroke="#38a169" 
                                  strokeWidth={3}
                                  activeDot={{ r: 8 }} 
                                  name="Profile Views" 
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="lastMonthShares" 
                                  stroke="#3182ce" 
                                  strokeWidth={2}
                                  name="Share Button Clicks" 
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="chart-placeholder">
                              <p>📈 Last month engagement data will appear here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Current Month Engagement */}
                    <div className="col-md-12" style={{ marginTop: '20px' }}>
                      <div className="chart-card large" style={{ minHeight: '400px' }}>
                        <div className="chart-header">
                          <h4>📈 Current Month Engagement</h4>
                          <span className="chart-subtitle">Profile views and share clicks trend for current month</span>
                        </div>
                        <div className="chart-container" style={{ height: '300px', width: '100%', marginTop: '20px' }}>
                          {engagementData && engagementData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={engagementData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="day" tick={{ fill: '#718096', fontSize: 12 }} />
                                <YAxis tick={{ fill: '#718096', fontSize: 12 }} />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                  }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                <Line 
                                  type="monotone" 
                                  dataKey="currentMonthViews" 
                                  stroke="#38a169" 
                                  strokeWidth={3}
                                  activeDot={{ r: 8 }} 
                                  name="Profile Views" 
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="currentMonthShares" 
                                  stroke="#3182ce" 
                                  strokeWidth={2}
                                  name="Share Button Clicks" 
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="chart-placeholder">
                              <p>📈 Current month engagement data will appear here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Services & Activities Row */}
                  <div className="row" style={{ marginTop: '20px' }}>
                    {/* Top Services */}
                    <div className="col-md-6">
                      <div className="chart-card">
                        <div className="chart-header">
                          <h4>� Top Services</h4>
                          <span className="chart-subtitle">Your most popular offerings</span>
                        </div>
                        <div className="top-services">
                          {serviceStats?.topServices?.length ? (
                            serviceStats.topServices.slice(0, 5).map((service, index) => (
                              <div key={index} className="service-item">
                                <div className="service-rank">#{index + 1}</div>
                                <div className="service-info">
                                  <div className="service-name">{service.title}</div>
                                  <div className="service-stats">
                                    {formatCurrency(service.price)} • {service.booking_count} bookings
                                    {service.avg_rating > 0 && (
                                      <span className="service-rating">
                                        ⭐ {service.avg_rating}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="empty-state">
                              <p>🎯 No services data yet</p>
                              <p>Create services to see analytics here</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Recent Activities */}
                    <div className="col-md-6">
                      <div className="chart-card">
                        <div className="chart-header">
                          <h4>🔔 Recent Activities</h4>
                          <span className="chart-subtitle">Latest updates</span>
                        </div>
                        <div className="activities-feed">
                          {activities.length ? (
                            activities.slice(0, 8).map((activity, index) => (
                              <div key={index} className="activity-item">
                                <div className="activity-icon">
                                  {getActivityIcon(activity.type)}
                                </div>
                                <div className="activity-content">
                                  <div className="activity-message">{activity.message}</div>
                                  <div className="activity-time">{formatDate(activity.timestamp)}</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="empty-state">
                              <p>📝 No recent activities</p>
                              <p>Activities will appear as you get bookings and reviews</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentDashboard; 